"use client";

import { useEffect, useRef } from "react";
import { isWindowDefined } from "@huub-web/utils";
import { useDebug } from "@huub-web/ui";

import { type GTMParams, setCurrDataLayerName } from "../../services/gtm";

/**
 * Props for the GTM component
 */
export type GTMProps = GTMParams & {
  /** GTM Container ID (e.g., 'GTM-XXXXXXX') */
  id: string;
};

/**
 * Universal GoogleTagManager component
 * Works with both Next.js and Vite using universal script injection
 */
export const GTM = ({
  id,
  gtmScriptUrl = "https://www.googletagmanager.com/gtm.js",
  dataLayerName = "dataLayer",
  auth,
  preview,
  dataLayer,
  nonce,
}: GTMProps) => {
  const hasInitialized = useRef(false);
  const { log } = useDebug();

  setCurrDataLayerName(dataLayerName);

  useEffect(() => {
    if (!isWindowDefined()) {
      return;
    }

    if (!id) {
      console.warn("[GTM] No GTM Container ID provided. Pass 'id' prop.");
      return;
    }

    if (hasInitialized.current) {
      return;
    }
    hasInitialized.current = true;

    // Build GTM URL parameters
    const gtmLayer = dataLayerName !== "dataLayer" ? `&l=${dataLayerName}` : "";
    const gtmAuth = auth ? `&gtm_auth=${auth}` : "";
    const gtmPreview = preview
      ? `&gtm_preview=${preview}&gtm_cookies_win=x`
      : "";

    // Performance mark for feature usage tracking
    if (typeof performance !== "undefined" && performance.mark) {
      performance.mark("mark_feature_usage", {
        detail: {
          feature: "universal-gtm",
        },
      });
    }

    // Initialize data layer and GTM
    const initScript = `
      (function(w,l){
        w[l]=w[l]||[];
        w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});
        ${dataLayer ? `w[l].push(${JSON.stringify(dataLayer)})` : ""}
      })(window,'${dataLayerName}');
    `;

    // Create initialization script.
    // `initScript` is built only from build-time GTM config (dataLayerName,
    // dataLayer) — never from user- or URL-derived input. Keep that invariant:
    // if any of these props ever become untrusted, this turns into a
    // script-injection sink. Assign via textContent (not innerHTML) so the
    // value is treated as script text and never parsed as HTML (SMA-2580).
    const initElement = document.createElement("script");
    initElement.id = "_universal-gtm-init";
    if (nonce) {
      initElement.nonce = nonce;
    }
    initElement.textContent = initScript;
    document.head.appendChild(initElement);

    // Create GTM script
    const gtmElement = document.createElement("script");
    gtmElement.id = "_universal-gtm";
    gtmElement.async = true;
    if (nonce) {
      gtmElement.nonce = nonce;
    }
    gtmElement.src = `${gtmScriptUrl}?id=${id}${gtmLayer}${gtmAuth}${gtmPreview}`;
    document.head.appendChild(gtmElement);

    log("cyan", "GTM", `Initialized Google Tag Manager '${id}'`);

    // Cleanup function
    return () => {
      if (!isWindowDefined()) {
        return;
      }

      const initScript = document.getElementById("_universal-gtm-init");
      const gtmScript = document.getElementById("_universal-gtm");
      if (initScript) {
        initScript.remove();
      }
      if (gtmScript) {
        gtmScript.remove();
      }
    };
  }, [id, gtmScriptUrl, dataLayerName, auth, preview, dataLayer, nonce]);

  return null;
};
